import React from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';

import { isAndroid } from '@helpers';
import { font } from '@styles/fonts';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { useManagePaidBarChartHeader } from '@components/ncFiles/components/paid/components/barChart/hooks/useManagePaidBarChartHeader';
import { dateFormat } from '@components/ncFiles/components/paid/components/barChart/helpers/dateFormat';
import { PaidBarChartHeaderProps, TimeElement } from '@components/ncFiles/components/paid/components/barChart/types';

const RenderTime = ({ time, color }: { time: TimeElement; color: string }) => {
  return (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
      }}
    >
      <View
        style={{
          flexDirection: 'row',
        }}
      >
        <Text
          style={[
            {
              color,
              fontVariant: ['tabular-nums'],
            },
            font['Large Title'],
          ]}
        >
          {Number(time?.h)}
          <Text
            style={[
              font['Button 1'],
              {
                color,
                fontVariant: ['tabular-nums'],
              },
              isAndroid && { lineHeight: 36 },
            ]}
          >
            {i18n.t('h')}{' '}
          </Text>
        </Text>
        {Number(time?.h) < 100 && (
          <Text
            style={[
              {
                color,
                fontVariant: ['tabular-nums'],
              },
              font['Large Title'],
            ]}
          >
            {Number(time?.m)}
            <Text
              style={[
                {
                  color,
                  fontVariant: ['tabular-nums'],
                },
                font['Button 1'],
                isAndroid && { lineHeight: 36 },
              ]}
            >
              {i18n.t('m')}
            </Text>
          </Text>
        )}
      </View>
    </View>
  );
};

const Average = ({
  date,
  C,
  color,
  showAverage,
}: {
  date?: string;
  C: any;
  color: string;
  showAverage: boolean;
}) => {
  const { timePeriod: selectedTime } = useHandleParams();

  return <Text style={[font['Body'], { color: C(color) }]}>{`${dateFormat({ date, selectedTime, showAverage })}`}</Text>;
};

export default ({
  item,
  C,
  barValueName,
  lineValueName,
  lineText,
  barText,
  lineColor,
  barColor,
  quantity,
  batches,
  showAverage,
  selectedBar,
  selectedBarIndex,
  barBlockWidth,
  chartHeight,
}: PaidBarChartHeaderProps) => {
  const { dataNotAvailable, leftElement, rightElement, lineLeft } = useManagePaidBarChartHeader({
    item,
    barValueName,
    lineValueName,
    selectedBar,
    selectedBarIndex,
    barBlockWidth,
  });

  return (
    <View testID={TEST_IDS.paidBarChartHeader.container} style={[{ width: '100%', padding: 5, marginBottom: 10, borderRadius: 5 }, selectedBarIndex ? { backgroundColor: C('Gray/Light/6') } : {}]}>
      <View style={{ width: '100%' }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <View testID={TEST_IDS.paidBarChartHeader.lineColumn} style={{ flex: 1 }}>
            <View>
              <Text style={[font['Headline'], { color: C(lineColor) }]}>{i18n.t(lineText)}</Text>
            </View>
            <RenderTime {...{ time: leftElement, loaded: !dataNotAvailable, color: C(lineColor) }} />
            <Average {...{ date: item?.date, C, color: lineColor, showAverage }} />
          </View>
          <View testID={TEST_IDS.paidBarChartHeader.barColumn} style={{ flex: 1 }}>
            <View>
              <Text style={[font['Headline'], { color: C(barColor) }]}>{i18n.t(barText)}</Text>
            </View>
            <RenderTime {...{ time: rightElement, loaded: !dataNotAvailable, color: C(barColor) }} />
            <Average {...{ date: item?.date, C, color: barColor, showAverage }} />
          </View>
        </View>
      </View>
      <View style={{ position: 'absolute', right: 0 }}>
        {Boolean(quantity) && (
          <View testID={TEST_IDS.paidBarChartHeader.quantity} style={{ flex: 1, alignItems: 'flex-end' }}>
            <Text style={[font['Headline'], { color: C(barColor), fontVariant: ['tabular-nums'] }]}>
              {i18n.t('pcs')} {selectedBar?.quantity || quantity}
            </Text>
          </View>
        )}
        {Boolean(batches) && (
          <View testID={TEST_IDS.paidBarChartHeader.batches} style={{ flex: 1, alignItems: 'flex-end' }}>
            <Text style={[font['Headline'], { color: C(barColor), fontVariant: ['tabular-nums'] }]}>
              {i18n.t('batches')} {selectedBar?.productionBatchCount || batches}
            </Text>
          </View>
        )}
      </View>
      {selectedBar?.height && (
        <View
          testID={TEST_IDS.paidBarChartHeader.verticalLine}
          style={{
            position: 'absolute',
            width: 1,
            backgroundColor: C('Label/Light/Tertiary'),
            height: chartHeight - (selectedBar?.height / 100) * chartHeight + 4,
            top: 90,
            left: lineLeft,
            zIndex: 1000,
          }}
        />
      )}
    </View>
  );
};
